import React, { useState, useMemo } from 'react';
import { Question } from '../../../types/game';
import './PairMatching.css';

interface PairMatchingProps {
  question: Question;
  selectedPairs: Record<string, string>;
  onPairSelect: (leftItem: string, rightItem: string) => void;
  answerSubmitted: boolean;
  hostIsPlaying?: boolean;
  gameType?: string;
}

/**
 * Get theme variant from gameType
 */
const getThemeVariant = (gameType?: string): 'blitz' | 'team' | 'family' | 'default' => {
  if (gameType === 'pair-matching') return 'blitz';
  if (gameType === 'team-pair-matching') return 'team';
  if (gameType === 'family-pair-matching') return 'family';
  return 'default';
};

/** Fixed colors for selected pairs: yellow, orange, blue, purple (no red/error, no double yellow) */
const PAIR_SELECTION_COLORS_RGB: string[] = [
  '234, 179, 8',   // yellow
  '249, 115, 22',  // orange
  '59, 130, 246',  // blue
  '168, 85, 247',  // purple
];

/**
 * Get theme colors for pair matching: use fixed selection colors for blitz/team/family
 * so pairs are always yellow, orange, blue, purple (consistent and not error-like).
 */
const getThemeColors = (variant: 'blitz' | 'team' | 'family' | 'default'): string[] => {
  if (variant === 'blitz' || variant === 'team' || variant === 'family') {
    return [...PAIR_SELECTION_COLORS_RGB];
  }
  if (typeof window === 'undefined') {
    return ['52, 211, 153'];
  }
  const root = getComputedStyle(document.documentElement);
  const primaryRgb = root.getPropertyValue('--color-primary-rgb').trim() || '52, 211, 153';
  return [primaryRgb];
};

/**
 * Generate color from RGB string
 * @param rgb - RGB values as string "r, g, b"
 * @returns Color object with primary, border, and hover colors
 */
const getColorFromRgb = (rgb: string) => {
  const [r, g, b] = rgb.split(',').map(v => parseInt(v.trim()));
  
  return {
    primary: `rgba(${r}, ${g}, ${b}, 0.3)`,
    border: `rgb(${r}, ${g}, ${b})`,
    hover: `rgba(${r}, ${g}, ${b}, 0.4)`,
  };
};

/**
 * Generate progressively darker shades of the theme color (for non-blitz/team games)
 * @param baseRgb - RGB values as string "r, g, b"
 * @param index - Index of the pair (0 = lightest, higher = darker)
 * @returns Color object with primary, border, and hover colors
 */
const getThemeColorShade = (baseRgb: string, index: number) => {
  const [r, g, b] = baseRgb.split(',').map(v => parseInt(v.trim()));
  
  // Calculate darker shade: reduce brightness by 15% per index (max 60% reduction)
  const darkenFactor = Math.min(0.15 * index, 0.6);
  const newR = Math.max(0, Math.round(r * (1 - darkenFactor)));
  const newG = Math.max(0, Math.round(g * (1 - darkenFactor)));
  const newB = Math.max(0, Math.round(b * (1 - darkenFactor)));
  
  return {
    primary: `rgba(${newR}, ${newG}, ${newB}, 0.3)`,
    border: `rgb(${newR}, ${newG}, ${newB})`,
    hover: `rgba(${newR}, ${newG}, ${newB}, 0.4)`,
  };
};

/**
 * Component for displaying and selecting pairs in pair matching game
 * Shows on player screen: interactive pairs interface for matching
 */
export const PairMatching: React.FC<PairMatchingProps> = ({
  question,
  selectedPairs,
  onPairSelect,
  answerSubmitted,
  hostIsPlaying = false,
  gameType,
}) => {
  const leftItems = question.leftItems || [];
  // Safeguard: never show more right options than left (backend reconnection now sends leftItems/rightItems; slice prevents stale/wrong data from showing 5 when there are 4 left)
  const rightItems = (question.rightItems || []).slice(0, leftItems.length);
  const isClickable = !answerSubmitted;

  // Track which left item is currently selected (waiting for right item)
  const [selectedLeftItem, setSelectedLeftItem] = useState<string | null>(null);

  // Get theme variant and colors
  const themeVariant = getThemeVariant(gameType);
  const themeColors = useMemo(() => getThemeColors(themeVariant), [themeVariant]);
  const useDifferentColors = themeVariant === 'blitz' || themeVariant === 'team' || themeVariant === 'family';
  
  // Calculate which color each pair should have
  const pairColors = useMemo(() => {
    const colors: Record<string, { left: number; right: number }> = {};
    let colorIndex = 0;
    
    // Assign colors to completed pairs
    leftItems.forEach((leftItem) => {
      const rightItem = selectedPairs[leftItem];
      if (rightItem && rightItem !== '') {
        colors[leftItem] = { left: colorIndex, right: colorIndex };
        colors[rightItem] = { left: colorIndex, right: colorIndex };
        colorIndex++;
      }
    });
    
    return { colors, nextColorIndex: colorIndex };
  }, [selectedPairs, leftItems]);

  const handleLeftItemClick = (leftItem: string) => {
    if (!isClickable) return;
    
    // If clicking the same item, deselect it
    if (selectedLeftItem === leftItem) {
      setSelectedLeftItem(null);
      return;
    }
    
    // If this left item already has a pair, remove it first
    if (selectedPairs[leftItem] && selectedPairs[leftItem] !== '') {
      onPairSelect(leftItem, '');
    }
    
    // Select the new left item (this will deselect any previously selected left item)
    setSelectedLeftItem(leftItem);
  };

  const handleRightItemClick = (rightItem: string) => {
    if (!isClickable) return;
    
    // If a left item is selected, create the pair
    if (selectedLeftItem) {
      // If this right item is already paired with another left item, remove that pair first
      const existingLeftItem = Object.keys(selectedPairs).find(
        left => selectedPairs[left] === rightItem && left !== selectedLeftItem
      );
      if (existingLeftItem) {
        onPairSelect(existingLeftItem, '');
        // Clear selectedLeftItem if it was pointing to the item we just unpaired
        if (selectedLeftItem === existingLeftItem) {
          setSelectedLeftItem(null);
        }
      }
      
      // Create the new pair
      onPairSelect(selectedLeftItem, rightItem);
      setSelectedLeftItem(null);
    } else {
      // If no left item is selected, clicking a right item should remove its pair if it exists
      const existingLeftItem = Object.keys(selectedPairs).find(
        left => selectedPairs[left] === rightItem
      );
      if (existingLeftItem) {
        // Remove the pair
        onPairSelect(existingLeftItem, '');
        // Clear selectedLeftItem if it was pointing to the item we just unpaired
        if (selectedLeftItem === existingLeftItem) {
          setSelectedLeftItem(null);
        }
      }
    }
  };

  // Get the color for a left item
  const getLeftItemColor = (leftItem: string) => {
    if (selectedLeftItem === leftItem) {
      // Currently selected, waiting for right item - use next available color
      if (useDifferentColors) {
        const colorIndex = pairColors.nextColorIndex % themeColors.length;
        return getColorFromRgb(themeColors[colorIndex]);
      } else {
        return getThemeColorShade(themeColors[0], pairColors.nextColorIndex);
      }
    }
    
    const pairColor = pairColors.colors[leftItem];
    if (pairColor !== undefined) {
      if (useDifferentColors) {
        const colorIndex = pairColor.left % themeColors.length;
        return getColorFromRgb(themeColors[colorIndex]);
      } else {
        return getThemeColorShade(themeColors[0], pairColor.left);
      }
    }
    
    return null;
  };

  // Get the color for a right item
  const getRightItemColor = (rightItem: string) => {
    const pairColor = pairColors.colors[rightItem];
    if (pairColor !== undefined) {
      if (useDifferentColors) {
        const colorIndex = pairColor.right % themeColors.length;
        return getColorFromRgb(themeColors[colorIndex]);
      } else {
        return getThemeColorShade(themeColors[0], pairColor.right);
      }
    }
    
    return null;
  };

  // Check if a right item is already paired
  const isRightItemPaired = (rightItem: string) => {
    return Object.values(selectedPairs).includes(rightItem);
  };

  return (
    <div className="pair-matching-container" data-testid="pair-matching-root">
      <div className="pair-matching-buttons-layout">
        {/* Left column */}
        <div className="pair-matching-column pair-matching-left-column">
          <div className="pair-matching-items-list">
            {leftItems.map((leftItem, index) => {
              const color = getLeftItemColor(leftItem);
              const isPaired = selectedPairs[leftItem] && selectedPairs[leftItem] !== '';
              const isSelected = selectedLeftItem === leftItem;
              
              return (
                <button
                  key={`left-${leftItem}`}
                  type="button"
                  className={`pair-matching-button pair-matching-left-button ${isSelected ? 'pair-matching-button--selected' : ''} ${isPaired ? 'pair-matching-button--paired' : ''}`}
                  onClick={() => handleLeftItemClick(leftItem)}
                  disabled={!isClickable}
                  style={{
                    backgroundColor: color ? color.primary : 'rgba(248, 250, 252, 0.15)',
                    borderColor: color ? color.border : 'rgba(248, 250, 252, 0.3)',
                    borderWidth: isSelected ? '3px' : '2px',
                    cursor: isClickable ? 'pointer' : 'not-allowed',
                    opacity: answerSubmitted ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (isClickable && !isSelected) {
                      e.currentTarget.style.backgroundColor = color 
                        ? color.hover 
                        : 'rgba(248, 250, 252, 0.25)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = color 
                        ? color.primary 
                        : 'rgba(248, 250, 252, 0.15)';
                    }
                  }}
                >
                  {leftItem}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right column */}
        <div className="pair-matching-column pair-matching-right-column">
          <div className="pair-matching-items-list">
            {rightItems.map((rightItem, index) => {
              const color = getRightItemColor(rightItem);
              const isPaired = isRightItemPaired(rightItem);
              
              return (
                <button
                  key={`right-${rightItem}`}
                  type="button"
                  className={`pair-matching-button pair-matching-right-button ${isPaired ? 'pair-matching-button--paired' : ''}`}
                  onClick={() => handleRightItemClick(rightItem)}
                  disabled={!isClickable}
                  style={{
                    backgroundColor: color ? color.primary : 'rgba(248, 250, 252, 0.15)',
                    borderColor: color ? color.border : 'rgba(248, 250, 252, 0.3)',
                    borderWidth: '2px',
                    cursor: isClickable ? 'pointer' : 'not-allowed',
                    opacity: answerSubmitted ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (isClickable) {
                      e.currentTarget.style.backgroundColor = color 
                        ? color.hover 
                        : 'rgba(248, 250, 252, 0.25)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = color 
                      ? color.primary 
                      : 'rgba(248, 250, 252, 0.15)';
                  }}
                >
                  {rightItem}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {hostIsPlaying && (
        <div className="pair-matching-info">
          <p>Selected pairs: {Object.keys(selectedPairs).filter(key => selectedPairs[key]).length} / {leftItems.length}</p>
        </div>
      )}
    </div>
  );
};
